import Formatters from "@/utils/Formatters";
import {compareStr} from "@/utils/compareStrings";
import { ORGON_GROUPS, CORNEA_SLUG } from "../constants/captacaoConstants";

export function buildPayload(captationData) {  
  return compareStr(captationData.realized, "naohouvecaptacao") ? buildNotRealized(captationData) : buildRealized(captationData);
}
 
function buildRealized(captationData) { 
  const { captationsForm, resource, currentUser, selectedFabricConditionings, isEdit } = captationData;
  
  const payload = {
    type_captation_id: captationData.type_captation_id,
    donor_id: resource.id,
    user_id: currentUser.id,
    date_hour: Formatters.formatAtualDate(),
    start_surgery: Formatters.isoDateTime(captationsForm.start_surgery),
    aortic_clamping: Formatters.isoDateTime(captationsForm.aortic_clamping),
    perfusion_time: captationsForm.perfusion_time,
    end_surgery: Formatters.isoDateTime(captationsForm.end_surgery),
    reason_not_captured_id: captationData.captationsForm.reason_not_captured_id,
    reason_not_captured: captationData.captationsForm.family_reason_description,
    surgery_description: null,
    fabric_conditioning: selectedFabricConditionings,
    blood_sample_collection: buildBloodCollection(captationData),
    temperature_controls: buildTemperatureControls(captationData),
    orgons: buildOrgons(captationData),
    corneas: buildCorneas(captationData),
  };

  if(isEdit) {
    payload.id = resource.captation_id;
  }

  return payload;
}

function buildBloodCollection(captationData) {
  const { bloodCollection, captation, isEdit } = captationData;
  const base = {
    blood_sample_collection_id: bloodCollection.blood_sample_collection_id,
    description: bloodCollection.blood_sample_collection_id == 6 ? bloodCollection.description : null,
    needle_gauge: bloodCollection.needle_gauge,
  };

  if(isEdit && captation && captation.blood_collection_captations && captation.blood_collection_captations.length) {
    base.id = captation.blood_collection_captations[0].id;
  }

  return [base];
}

function buildTemperatureControls(captationData) {
  const { fabric_conditioning, captation, isEdit } = captationData;
  const controls = [
    {
      type_temperature_control_id: 1,
      temperature_date_and_time: Formatters.isoDateTime(fabric_conditioning.temperature_date_and_time_saida),
      current_temperature: fabric_conditioning.current_temperature_saida,
    },
    { type_temperature_control_id: 2 },
  ];

  if (isEdit && captation && captation.temperature_controls && captation.temperature_controls.length) {
    controls[0].id = captation.temperature_controls[0].id;
    controls[1].id = captation.temperature_controls[1] ? captation.temperature_controls[1].id : undefined;
  }

  return controls;
}

function buildOrgons(captationData) {
  return captationData.orgons.filter(orgon => !isCornea(orgon))
    .map(orgon => mapOrgons(orgon, captationData));
}

function buildCorneas(captationData) {
  return captationData.orgons.filter(orgon => isCornea(orgon)).map(orgon => ({
    ...mapOrgons(orgon, captationData),
    surgical_description: buildSurgicalDescription(orgon, captationData.isEdit),
  }));
}

function buildSurgicalDescription(orgon, isEdit) {
  if (!orgon.surgical_description) return [];
  return orgon.surgical_description.map(surgical_description => {
    const base = {
      surgical_description_id: surgical_description.id || surgical_description,
      description: surgical_description.description || "",
    };
    if (isEdit && surgical_description.orgon_surgical_description_id) {
      base.id = surgical_description.orgon_surgical_description_id;
    }
    return base;
  });
}

function mapOrgons(orgon, captationData) {
  const { isEdit, captation, resource, blockDataForm } = captationData;
  
  const slug = (orgon.orgon && orgon.orgon.slug) || orgon.slug;
  const statusSlug = orgon.orgon_captured_status_id;
  const isBlock = statusSlug === "captado-em-bloco";
  const isCaptured = statusSlug === "captado" || isBlock;
  const group = getOrgonGroup(slug);
  const src = isBlock ? (blockDataForm[group] || {}) : orgon; 
  
  const base = {
    orgon_captured_status_id: isCaptured ? 1 : 2,
    preservation_solutions_id: isCaptured ? src.preservation_solutions_id : null,
    orgon_id: orgon.orgon_id || orgon.id,
    unity_id: orgon.unity_id || null,
    packed: isCaptured ? src.packed : null,
    lung_trunk: isCaptured ? orgon.lung_trunk : null,
    quantity: isCaptured ? orgon.quantity : null,
    captation_hour: isCaptured ? Formatters.isoDateTime(orgon.captation_hour) : null,
    biopsy: isCaptured ? src.biopsy : null,
    number_lot: isCaptured ? src.number_lot : null,
    lot_expiration_date: isCaptured ? src.lot_expiration_date : null,
    reason_not_captured_id: statusSlug === "nao-captado" ? orgon.reason_not_captured_id : null,
    em_bloco_flg: isBlock ? "SIM" : "NÃO",
  };

  if(captation && isEdit && resource.donor_status_slug != "doacao-nao-efetivada") {
    base.orgon_captured_id = orgon.id;
  }

  if(isBlock && !isEdit) {
    const nephKey =
      slug === "rim-esquerdo" ? "nephrectomy_left" :
      slug === "rim-direito"  ? "nephrectomy_right" : null;
    const blockData = blockDataForm[group] || {};
    if (nephKey && blockData[nephKey]) {
      base.nephrectomy = [buildNephrectomy(blockData[nephKey], isEdit)];
    }
  } else if (orgon.normal_morphology) {
    base.nephrectomy = [buildNephrectomy(orgon, isEdit)];
  }

  return base;
}

export function getOrgonGroup(slug) {
  return Object.keys(ORGON_GROUPS).find(group => ORGON_GROUPS[group].includes(slug));
}

function isCornea(orgon) {
  const slug = (orgon.orgon && orgon.orgon.slug) || orgon.slug;
  return CORNEA_SLUG.includes(slug);
}

function buildNephrectomy(item, isEdit) {
  const nephrectomy = {
    normal_morphology: item.normal_morphology,
    single_arterial_pedicle: item.single_arterial_pedicle,
    single_venous_pedicle: item.single_venous_pedicle,
    single_ureteral_rout: item.single_ureteral_rout,
    adequate_perfusion: item.adequate_perfusion,
  };
  if (isEdit && item.nephrectomy && item.nephrectomy.length) {
    nephrectomy.nephrectomy_id = item.nephrectomy[0].id;
  }
  return nephrectomy;
}
   
function buildNotRealized(captationData) {
  const { captationsForm, resource, currentUser } = captationData; 
  return {
    donor_id: resource.id,
    user_id: currentUser.id,
    date_hour: null,
    start_surgery: null,
    aortic_clamping: null,
    perfusion_time: null,
    end_surgery: null,
    reason_not_captured_id: captationsForm.reason_not_captured_id,
    reason_not_captured: captationsForm.reason_not_captured,
    surgery_description: null,
    type_captation_id: captationData.type_captation_id,
  };
}
 

